import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import { SITE, NAV } from "../lib/site";

// Footer is dark green — the green-on-cream brand logo wouldn't show, so we use a
// cream wordmark with a small daisy mark for the same brand cue.
const DaisyDot = () => (
  <span aria-hidden className="inline-block relative w-7 h-7 align-middle mr-2">
    <span className="absolute inset-0 grid place-items-center">
      <span className="w-3 h-3 rounded-full bg-daisy" />
    </span>
    <svg viewBox="0 0 32 32" className="absolute inset-0 w-7 h-7">
      <g fill="#FDFBF7">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <ellipse key={deg} cx="16" cy="6" rx="3" ry="6" transform={`rotate(${deg} 16 16)`} />
        ))}
      </g>
    </svg>
  </span>
);

const LEGAL = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/cookies", label: "Cookie Policy" },
];

const Footer = () => (
  <footer className="bg-forest text-cream mt-24 pb-24 lg:pb-12" data-testid="site-footer">
    <div className="container-soft py-14 grid gap-12 md:grid-cols-3">
      <div>
        <div className="flex items-center">
          <DaisyDot />
          <span className="font-serif text-3xl text-cream">
            Daisy <span className="italic">Does</span>
          </span>
        </div>
        <p className="mt-4 text-cream/85 max-w-sm">
          Admin, applications and everyday support. Friendly, practical help from Sarah for elderly clients, families and busy households.
        </p>
        <p className="mt-4 italic text-daisy">"Practical help, calmly done."</p>
      </div>

      <div>
        <h3 className="text-cream font-serif text-2xl mb-4">Explore</h3>
        <ul className="space-y-2">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link
                to={n.to}
                className="hover:text-daisy transition-colors"
                data-testid={`footer-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </Link>
            </li>
          ))}
        </ul>

        <h3 className="text-cream font-serif text-2xl mt-8 mb-4">Legal</h3>
        <ul className="space-y-2">
          {LEGAL.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                className="hover:text-daisy transition-colors"
                data-testid={`footer-legal-${l.to.slice(1)}`}
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-cream font-serif text-2xl mb-4">Speak to Sarah</h3>
        <a href={SITE.tel} className="flex items-center gap-3 hover:text-daisy" data-testid="footer-phone">
          <Phone className="w-5 h-5" /> {SITE.phoneDisplay}
        </a>
        <a
          href={`mailto:${SITE.email}`}
          className="mt-3 flex items-center gap-3 hover:text-daisy"
          data-testid="footer-email"
        >
          <Mail className="w-5 h-5" /> {SITE.email}
        </a>
        <Link
          to="/contact#booking"
          className="mt-6 inline-flex btn-secondary h-12 text-base"
          data-testid="footer-book-button"
        >
          Book a Free Call
        </Link>
      </div>
    </div>

    <div className="border-t border-cream/15">
      <div className="container-soft py-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-sm text-cream/75">
        <p>© {new Date().getFullYear()} Daisy Does. All rights reserved.</p>
        <p>
          Daisy Does provides practical administrative support only. We do not provide legal, financial, tax or regulated benefits advice, and we cannot guarantee the outcome of any application or process.
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
